import type { ButtonProps } from '@mui/material/Button';
import type { Theme, CSSObject, Components, ComponentsVariants } from '@mui/material/styles';

import { buttonClasses } from '@mui/material/Button';
import { loadingButtonClasses } from '@mui/lab/LoadingButton';

import { varAlpha, stylesMode } from '../../styles';

// ----------------------------------------------------------------------

// NEW VARIANT
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true;
  }
}

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

type ColorType = (typeof COLORS)[number];

const buttonTransitions = (theme: Theme) =>
  theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'transform'], {
    duration: theme.transitions.duration.shortest,
  });

const softShadow = (theme: Theme, color = theme.vars.palette.grey['500Channel']) =>
  `inset 0 0 0 1px ${varAlpha(color, 0.12)}`;

const focusRing = (theme: Theme, color = theme.vars.palette.primary.mainChannel) =>
  `0 0 0 3px ${varAlpha(color, 0.18)}`;

function styleColors(ownerState: ButtonProps, styles: (val: ColorType) => CSSObject) {
  const outputStyle = COLORS.reduce((acc, color) => {
    if (!ownerState.disabled && ownerState.color === color) {
      acc = styles(color);
    }
    return acc;
  }, {});

  return outputStyle;
}

// ----------------------------------------------------------------------

const MuiButtonBase: Components<Theme>['MuiButtonBase'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      fontFamily: theme.typography.fontFamily,
    }),
  },
};

// ----------------------------------------------------------------------

const softVariant: Record<string, ComponentsVariants<Theme>['MuiButton']> = {
  colors: COLORS.map((color) => ({
    props: ({ ownerState }) =>
      !ownerState.disabled && ownerState.variant === 'soft' && ownerState.color === color,
    style: ({ theme }) => ({
      color: theme.vars.palette[color].dark,
      boxShadow: softShadow(theme, theme.vars.palette[color].mainChannel),
      backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.1),
      '&:hover': {
        boxShadow: softShadow(theme, theme.vars.palette[color].mainChannel),
        backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.18),
      },
      '&.Mui-focusVisible': {
        boxShadow: `${softShadow(theme, theme.vars.palette[color].mainChannel)}, ${focusRing(
          theme,
          theme.vars.palette[color].mainChannel
        )}`,
      },
      [stylesMode.dark]: { color: theme.vars.palette[color].light },
    }),
  })),
  base: [
    {
      props: ({ ownerState }) => ownerState.variant === 'soft',
      style: ({ theme }) => ({
        color: theme.vars.palette.text.secondary,
        boxShadow: softShadow(theme),
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
        '&:hover': {
          color: theme.vars.palette.text.primary,
          boxShadow: softShadow(theme, theme.vars.palette.grey['500Channel']),
          backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.14),
        },
        [`&.${buttonClasses.disabled}`]: {
          color: theme.vars.palette.action.disabled,
          boxShadow: 'none',
          backgroundColor: theme.vars.palette.action.disabledBackground,
        },
        [`& .${loadingButtonClasses.loadingIndicatorStart}`]: { left: 14 },
        [`& .${loadingButtonClasses.loadingIndicatorEnd}`]: { right: 14 },
        [`&.${buttonClasses.sizeSmall}`]: {
          [`& .${loadingButtonClasses.loadingIndicatorStart}`]: { left: 10 },
          [`& .${loadingButtonClasses.loadingIndicatorEnd}`]: { right: 10 },
        },
      }),
    },
  ],
};

const MuiButton: Components<Theme>['MuiButton'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { color: 'inherit', disableElevation: true },

  /** **************************************
   * VARIANTS
   *************************************** */
  variants: [
    /**
     * @variant soft
     */
    ...[...softVariant.base!, ...softVariant.colors!],
  ],

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape.borderRadius,
      fontWeight: theme.typography.fontWeightSemiBold,
      letterSpacing: 0,
      boxShadow: 'none',
      transition: buttonTransitions(theme),
      '&.Mui-focusVisible': {
        boxShadow: focusRing(theme),
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      [`&.${buttonClasses.disabled}`]: {
        boxShadow: 'none',
      },
      [`& .${buttonClasses.startIcon}, & .${buttonClasses.endIcon}`]: {
        [`& > *:nth-of-type(1)`]: {
          fontSize: '1.125rem',
        },
      },
    }),
    /**
     * @variant contained
     */
    contained: ({ theme, ownerState }) => {
      const styled = {
        colors: styleColors(ownerState, (color) => ({
          boxShadow: `0 1px 2px 0 ${varAlpha(theme.vars.palette[color].mainChannel, 0.2)}`,
          '&:hover': {
            boxShadow: `0 8px 16px -8px ${varAlpha(theme.vars.palette[color].mainChannel, 0.56)}`,
            transform: 'translateY(-1px)',
          },
          '&.Mui-focusVisible': {
            boxShadow: focusRing(theme, theme.vars.palette[color].mainChannel),
          },
        })),
        inheritColor: {
          ...(ownerState.color === 'inherit' &&
            !ownerState.disabled && {
              color: theme.vars.palette.common.white,
              backgroundColor: theme.vars.palette.grey[800],
              boxShadow: `0 1px 2px 0 ${varAlpha(theme.vars.palette.grey['500Channel'], 0.24)}`,
              '&:hover': {
                boxShadow: `0 8px 16px -8px ${varAlpha(
                  theme.vars.palette.grey['800Channel'],
                  0.56
                )}`,
                backgroundColor: theme.vars.palette.grey[700],
                transform: 'translateY(-1px)',
              },
              [stylesMode.dark]: {
                color: theme.vars.palette.grey[800],
                backgroundColor: theme.vars.palette.common.white,
                '&:hover': {
                  backgroundColor: theme.vars.palette.grey[400],
                  boxShadow: `0 8px 16px -8px ${varAlpha(
                    theme.vars.palette.common.whiteChannel,
                    0.32
                  )}`,
                },
              },
            }),
        },
      };
      return { ...styled.inheritColor, ...styled.colors };
    },
    /**
     * @variant outlined
     */
    outlined: ({ theme, ownerState }) => {
      const styled = {
        colors: styleColors(ownerState, (color) => ({
          color: theme.vars.palette[color].main,
          borderColor: varAlpha(theme.vars.palette[color].mainChannel, 0.32),
          backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.02),
          '&:hover': {
            borderColor: theme.vars.palette[color].main,
            backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.08),
          },
          '&.Mui-focusVisible': {
            boxShadow: focusRing(theme, theme.vars.palette[color].mainChannel),
          },
        })),
        inheritColor: {
          ...(ownerState.color === 'inherit' &&
            !ownerState.disabled && {
              borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.24),
              backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.02),
              '&:hover': {
                borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.48),
                backgroundColor: theme.vars.palette.action.hover,
              },
            }),
        },
        base: {
          boxShadow: 'none',
        },
      };
      return { ...styled.base, ...styled.inheritColor, ...styled.colors };
    },
    /**
     * @variant text
     */
    text: ({ ownerState, theme }) => {
      const styled = {
        inheritColor: {
          ...(ownerState.color === 'inherit' &&
            !ownerState.disabled && {
              color: theme.vars.palette.text.secondary,
              '&:hover': {
                color: theme.vars.palette.text.primary,
                backgroundColor: theme.vars.palette.action.hover,
              },
            }),
        },
        colors: styleColors(ownerState, (color) => ({
          '&:hover': {
            backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.08),
          },
          '&.Mui-focusVisible': {
            boxShadow: focusRing(theme, theme.vars.palette[color].mainChannel),
          },
        })),
      };
      return { ...styled.inheritColor, ...styled.colors };
    },
    /**
     * @size
     */
    sizeSmall: ({ ownerState }) => ({
      height: 32,
      ...(ownerState.variant === 'text'
        ? { paddingLeft: '6px', paddingRight: '6px' }
        : { paddingLeft: '10px', paddingRight: '10px' }),
    }),
    sizeMedium: ({ ownerState }) => ({
      minHeight: 38,
      ...(ownerState.variant === 'text'
        ? { paddingLeft: '8px', paddingRight: '8px' }
        : { paddingLeft: '14px', paddingRight: '14px' }),
    }),
    sizeLarge: ({ ownerState }) => ({
      height: 48,
      ...(ownerState.variant === 'text'
        ? { paddingLeft: '10px', paddingRight: '10px' }
        : { paddingLeft: '16px', paddingRight: '16px' }),
    }),
  },
};

// ----------------------------------------------------------------------

export const button = { MuiButtonBase, MuiButton };
